import { browserFetch } from '../../services/browserSession';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
   Users,
   Search,
   Filter,
   CheckCircle2,
   Sparkles,
   ChevronRight,
   TrendingUp,
   Instagram,
} from 'lucide-react';
import Button from '../common/design-system/Button';
import { Badge } from '../common/design-system/Badge';
import Skeleton from '../common/design-system/Skeleton';
import styles from './BusinessApplications.module.scss';
import clsx from 'clsx';
import Avatar from '../common/Avatar';

const PAGE_POLL_INTERVAL_MS = 30000;

const BusinessApplications = () => {
   const navigate = useNavigate();

   // States
   const [applications, setApplications] = useState([]);
   const [offerDetails, setOfferDetails] = useState({});
   const [loading, setLoading] = useState(true);

   const [query, setQuery] = useState('');
   const [statusFilter, setStatusFilter] = useState('all');

   const fetchApplications = async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      try {

         const res = await browserFetch('/api/feed/applications/');

         if (!res.ok) throw new Error('Error al cargar aplicaciones.');
         const data = await res.json();
         const apps = Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : [];

         setApplications(apps);
         await groupAndFetchOfferDetails(apps);
      } catch (err) {
         console.error(err);
      } finally {
         if (!silent) setLoading(false);
      }
   };

   useEffect(() => {
      fetchApplications();

      const refresh = () => {
         if (!document.hidden) fetchApplications({ silent: true });
      };
      const interval = window.setInterval(refresh, PAGE_POLL_INTERVAL_MS);
      const handleVisibleRefresh = () => {
         if (!document.hidden) refresh();
      };

      window.addEventListener('focus', refresh);
      window.addEventListener('foth:applications-refresh', refresh);
      document.addEventListener('visibilitychange', handleVisibleRefresh);

      return () => {
         window.clearInterval(interval);
         window.removeEventListener('focus', refresh);
         window.removeEventListener('foth:applications-refresh', refresh);
         document.removeEventListener('visibilitychange', handleVisibleRefresh);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   const groupAndFetchOfferDetails = async (apps) => {
      const details = {};
      const offersToFetch = new Set(apps.map(app => app.offer || app.offer_id).filter(Boolean));

      await Promise.all(Array.from(offersToFetch).map(async (oid) => {
         try {
            const res = await browserFetch(`/api/collabs/collab/${oid}/`);
            if (res.ok) {
               const offerData = await res.json();
               details[oid] = {
                  ...offerData,
                  apps: apps.filter(app => (app.offer || app.offer_id) === oid)
               };
            }
         } catch (e) {
            console.error(`Error loading collab ${oid}`, e);
         }
      }));
      setOfferDetails(details);
   };

   const stats = useMemo(() => {
      const total = applications.length;
      const accepted = applications.filter(a => ['accepted', 'approved', 'confirmed'].includes(a.status)).length;
      const activeCollabs = Object.keys(offerDetails).length;
      return { total, accepted, activeCollabs };
   }, [applications, offerDetails]);

   const filteredGroups = useMemo(() => {
      return Object.values(offerDetails).filter(group => {
         const matchesQuery = group.title.toLowerCase().includes(query.toLowerCase()) ||
            group.apps.some(a => getCreatorName(a).toLowerCase().includes(query.toLowerCase()));

         const matchesStatus = statusFilter === 'all' ? true :
            group.apps.some(a => normalizeApplicationStatus(a.status) === statusFilter);

         return matchesQuery && matchesStatus;
      });
   }, [offerDetails, query, statusFilter]);

   const getCreatorName = (app) => {
      if (app.creator_profile?.full_name) return app.creator_profile.full_name;
      if (app.creator?.full_name) return app.creator.full_name;
      return app.creator_username || app.user?.username || 'Talento FOTH';
   };

   if (loading) return (
      <div className={styles['applicants-page']}>
         <div className={styles.container}>
            <div className="pt-20 space-y-10">
               <Skeleton width="60%" height="100px" />
               <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Skeleton height="120px" />
                  <Skeleton height="120px" />
                  <Skeleton height="120px" />
               </div>
            </div>
         </div>
      </div>
   );

   return (
      <div className={styles['applicants-page']}>
         <div className={styles.container}>
            {/* Hero Header (Aligned with Identity) */}
            <section className={styles['hero-section']}>
               <div className={styles['hero-inner']}>
                  <div className={styles['badge-pipeline']}>
                     <Sparkles size={14} /> Talent pipeline
                  </div>
                  <h1>Centro de Candidatos</h1>
                  <p>Gestiona todas las postulaciones de tus collabs en una sola vista. Evalúa perfiles y monitorea el ritmo de tus campañas en una experiencia editorial.</p>

                  <div className={styles['stats-grid']}>
                     <OverviewStat icon={Users} label="Total postulaciones" value={stats.total} helper="En todos tus collabs" />
                     <OverviewStat icon={CheckCircle2} label="Aceptadas" value={stats.accepted} helper="Talentos aprobados" />
                     <OverviewStat icon={TrendingUp} label="Collabs activos" value={stats.activeCollabs} helper="Con actividad reciente" accent />
                  </div>
               </div>
            </section>

            {/* Refined Filter Bar */}
            <div className={styles['filter-section']}>
               <div className={styles['search-wrapper']}>
                  <Search size={18} className={styles['search-icon']} />
                  <input
                     type="text"
                     placeholder="Buscar por collab o creador..."
                     value={query}
                     onChange={e => setQuery(e.target.value)}
                  />
               </div>
               <div className={styles['select-wrapper']}>
                  <Filter size={16} />
                  <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                     <option value="all">Todos los estados</option>
                     <option value="pending">Pendientes</option>
                     <option value="accepted">Seleccionados</option>
                     <option value="rejected">Rechazados</option>
                  </select>
               </div>
            </div>

            {/* Groups List */}
            <div className="space-y-6">
               {filteredGroups.length > 0 ? (
                  filteredGroups.map(group => (
                     <ApplicantsGroupCard key={group.id} group={group} creatorNameFn={getCreatorName} navigate={navigate} />
                  ))
               ) : (
                  <div className={styles['empty-state-card']}>
                     <Users className={styles.icon} size={48} />
                     <h3>Sin resultados</h3>
                     <p>No encontramos aplicaciones que coincidan con tus filtros actuales.</p>
                  </div>
               )}
            </div>
         </div>
      </div>

   );
};

const OverviewStat = ({ icon: Icon, label, value, helper, accent }) => (
   <div className={styles['stat-card']}>
      <div className={clsx(styles['stat-icon'], accent && styles.accent)}>
         <Icon size={24} />
      </div>
      <div className={styles['stat-info']}>
         <div className={styles.label}>{label}</div>
         <div className={styles.value}>{value}</div>
         <div className={styles.helper}>{helper}</div>
      </div>
   </div>
);

const ApplicantsGroupCard = ({ group, creatorNameFn, navigate }) => {
   return (
      <div className={styles['group-card']}>
         <div className={styles['group-header']}>
            <div className={styles['title-block']}>
               <h3>{group.title}</h3>
               <p>Estado general de postulaciones</p>
            </div>
            <div className={styles['progress-block']}>
               <div className={styles['progress-labels']}>
                  <span className={styles.filled}>{group.apps.length} de {group.quantity}</span>
                  <span className={styles.total}>cupos aplicados</span>
               </div>
               <div className={styles['progress-track']}>
                  <motion.div
                     className={styles['progress-fill']}
                     initial={{ width: 0 }}
                     animate={{ width: `${(group.apps.length / group.quantity) * 100}%` }}
                     transition={{ duration: 1 }}
                  />
               </div>
            </div>
         </div>

         <div className={styles['applications-table-header']}>
            <div>Creador</div>
            <div>Fecha de Aplicación</div>
            <div>Estado</div>
            <div>Acciones</div>
         </div>

         <div className="divide-y divide-slate-100">
            {group.apps.map(app => {
               const normalizedStatus = normalizeApplicationStatus(app.status);
               app = { ...app, status: normalizedStatus };

               return (
               <div key={app.id} className={styles['applicant-row']}>
                  <div className={styles['creator-info']}>
                     <div className={styles['avatar-box']}>
                        <Avatar
                           size="md"
                           src={app.creator_profile?.profile_picture_url || app.creator_profile?.avatar_url || app.creator_profile?.user?.profile_picture_url}
                           fallbackSrc={app.creator_profile?.ig_profile_picture_url}
                           name={creatorNameFn(app)}
                           alt={creatorNameFn(app)}
                        />
                     </div>
                     <span className={styles.name}>{creatorNameFn(app)}</span>
                  </div>
                  <div className={styles.date}>
                     {new Date(app.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}
                  </div>
                  <div>
                    <Badge variant={normalizedStatus === 'accepted' ? 'deliverable' : normalizedStatus === 'rejected' ? 'status-rejected' : 'invite'}>
                       {app.status === 'accepted' ? 'Seleccionado' : app.status === 'pending' ? 'Bajo revisión' : app.status}
                    </Badge>

                    {app.deliverables?.length > 0 && (
                      <div className="mt-1 flex items-center gap-1 text-[9px] font-bold text-pink-600 uppercase">
                         <Instagram size={8} /> Deliverable OK
                      </div>
                    )}
                 </div>
                  <div className="flex justify-end">
                     <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/business/collabs/${group.id}/applications`)}
                     >
                        Detalle <ChevronRight size={14} className="ml-1" />
                     </Button>
                  </div>
               </div>
               );
            })}
         </div>
      </div>
   );
};

const normalizeApplicationStatus = (status) => {
   if (status === 'applied' || status === 'pending') return 'pending';
   if (['accepted', 'approved', 'confirmed'].includes(status)) return 'accepted';
   return status;
};

export default BusinessApplications;
